// Press releases data structure
export interface PressRelease {
  id: string;
  slug: string;
  date: string;
  publishedAt: string; // ISO date string for consistent date handling
  title: string;
  category: string;
  excerpt: string;
  fullText: string;
  tags: string[];
  seoTitle?: string;
  seoDescription?: string;
  keywords?: string[];
  featured?: boolean;
  author?: string;
  readTime?: string;
}

export const pressReleases: PressRelease[] = [
  {
    id: 'beta-launch',
    slug: 'newsroom-launches-public-beta-ai-powered-market-research',
    date: 'March 28, 2024',
    publishedAt: '2024-03-28T10:00:00Z',
    title: 'Newsroom Launches Public Beta of AI-Powered Market Research Platform',
    category: 'Product Launch',
    tags: ['AI', 'Beta Launch', 'Market Research', 'Claude 3.5', 'FinTech'],
    excerpt: 'Revolutionary platform combines Claude 3.5 AI with real-time market data to democratize institutional-grade financial research and trading technology.',
    fullText: `VICTORIA, BC, March 28, 2024 -- Newsroom (news-room.ca) today announced the public beta launch of its groundbreaking AI-powered market research platform, designed to democratize access to institutional-grade financial analysis tools. The platform leverages Claude 3.5 AI technology to transform complex market data into actionable trading insights and custom widget generation.

"Today marks a significant milestone in our mission to level the playing field between individual and institutional investors," said Nik Zral, CEO and founder of Newsroom. "By combining advanced AI capabilities with real-time market data, we're making sophisticated financial analysis accessible to everyone."

Key Features of the Newsroom Platform:

• Prism AI Research Assistant: Powered by Claude 3.5, Prism analyzes terabytes of market data in real-time to provide comprehensive insights across stocks, sectors, and market trends.

• Instant Widget Generation: Users can describe their ideal trading widget in plain English and watch it materialize in seconds, complete with live data integration.

• Multi-Mode Analysis: Specialized research modes for technical analysis, fundamental research, and market sentiment provide targeted insights for different trading strategies.

• Real-Time Market Intelligence: Integration with premium data providers ensures users have access to the latest market movements, news, and analyst reports.

The public beta launch follows a successful private beta period that saw over 10,000 widgets generated and more than 100,000 market research queries processed. Early users have reported significant improvements in their trading research efficiency, with the platform reducing analysis time by up to 140x compared to traditional methods.

"Newsroom represents the future of market research," noted Marcus Rodriguez, CTO. "Our platform not only democratizes access to advanced trading technology but also helps traders make more informed decisions through AI-powered analysis of vast amounts of market data."

Market Impact and Vision:
The platform addresses a critical gap in the financial technology landscape, where sophisticated analysis tools have traditionally been reserved for institutional investors with access to expensive Bloomberg terminals and proprietary research platforms. By leveraging cutting-edge AI technology, Newsroom makes these capabilities accessible to individual traders, small investment firms, and financial advisors.

The company's vision extends beyond basic market data aggregation. Newsroom's AI-powered approach enables dynamic analysis that adapts to changing market conditions, providing users with contextual insights that would traditionally require teams of analysts to produce.

Technology Integration:
Built on a robust cloud infrastructure, the platform integrates with multiple data sources including:
- Real-time market data feeds from major exchanges
- Financial news and sentiment analysis
- SEC filings and regulatory documents  
- Social media and alternative data sources
- Technical indicator calculations and pattern recognition

The platform's AI engine processes this vast amount of information in real-time, presenting users with synthesized insights through an intuitive interface that requires no technical expertise to navigate.

Availability and Access:
The public beta is now available at news-room.ca. Users can request access through the platform's website and receive their beta invitation within 24 hours. The beta period will run through Q2 2024, with general availability planned for later this year.

About Newsroom:
Founded in 2021, Newsroom is revolutionizing financial market research through AI technology. The platform combines advanced artificial intelligence with real-time market data to provide institutional-grade analysis tools for traders of all levels. Based in Victoria, BC, the company is committed to democratizing access to sophisticated financial analysis tools and empowering individual investors with the same capabilities traditionally reserved for institutional players.

For more information about Newsroom and its AI-powered market research platform, visit news-room.ca or contact hello@news-room.ca.

Media Contact:
Newsroom Inc.
Email: hello@news-room.ca
Phone: +1 (778) 751-0983
Website: news-room.ca`,
    seoTitle: 'Newsroom Launches AI-Powered Market Research Platform - Public Beta Now Available',
    seoDescription: 'Newsroom announces public beta of revolutionary AI-powered market research platform combining Claude 3.5 AI with real-time market data to democratize institutional-grade financial analysis.',
    keywords: ['AI market research', 'public beta launch', 'Claude 3.5 AI', 'financial technology', 'trading platform', 'market intelligence', 'Newsroom'],
    featured: true,
    author: 'Newsroom Press Team',
    readTime: '3 min read'
  },
  
];

// Helper functions
export const getPressReleaseBySlug = (slug: string): PressRelease | undefined => {
  return pressReleases.find(release => release.slug === slug);
};

export const getFeaturedPressReleases = (): PressRelease[] => {
  return pressReleases.filter(release => release.featured);
};

export const getPressReleasesByCategory = (category: string): PressRelease[] => {
  return pressReleases.filter(release => release.category === category);
};
